import React, { useState, useMemo } from "react";
import { Container, Row, Col, Card, Form, InputGroup } from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Cash, Tree, Journals, Stack, Filter, Calendar3, PCircle, Stripe } from "react-bootstrap-icons";
import {Link} from "react-router-dom"
import "../App.css"


const Dashboard = ({ date, tema, partizi }) => {
  const { user } = useAuth();
  const isDark = tema === "dark";

  // --- STATE-URI ---
  const [filtruAn, setFiltruAn] = useState("Toti");
  const [filtruOcol, setFiltruOcol] = useState("Toate");
  const [filtruProprietar, setFiltruProprietar] = useState("Toti");
  const [filtruTip, setFiltruTip] = useState("Toate");
  const [filtruStoc, setFiltruStoc] = useState("Toate");

  // --- LOGICĂ FILTRARE ---
  const datePermise = useMemo(() => {
    if (!date) return [];
    return date.filter((item) => {
      if (user?.rol === "administrator") return true;
      return (
        item.ocol?.toString().trim().toLowerCase() ===
        user?.ocol?.toString().trim().toLowerCase()
      );
    });
  }, [date, user]);

  const dateFiltrateGlobal = useMemo(() => {
    return datePermise.filter((item) => {
      const dataRaw = item.dataCreare;
      let dataObiect = dataRaw?.seconds
        ? new Date(dataRaw.seconds * 1000)
        : dataRaw
          ? new Date(dataRaw)
          : new Date();
      const anContract = dataObiect.getFullYear().toString();

      let conditieStoc = true;
      if (filtruStoc !== "Toate") {
        const ids =
          item.partiziAferente || (item.partidaId ? [item.partidaId] : []);
        conditieStoc = ids.some((idCautat) => {
          const p = partizi?.find((x) => String(x.id) === String(idCautat));
          return (
            p?.stoc?.toString().toLowerCase().trim() ===
            filtruStoc.toLowerCase()
          );
        });
      }

      return (
        conditieStoc &&
        (filtruAn === "Toti" || anContract === filtruAn) &&
        (filtruOcol === "Toate" || item.ocol === filtruOcol) &&
        (filtruTip === "Toate" ||
          filtruTip === "Toti" ||
          item.tip === filtruTip) &&
        (filtruProprietar === "Toti" || item.proprietar === filtruProprietar)
      );
    });
  }, [datePermise, filtruAn, filtruOcol, filtruTip, filtruProprietar, filtruStoc, partizi ]);

  // --- CALCULE METRICI ---
  const totalVolum = useMemo(
    () =>
      dateFiltrateGlobal.reduce(
        (acc, curr) => acc + (parseFloat(curr.volum) || 0),
        0,
      ),
    [dateFiltrateGlobal],
  );
  const totalValoare = useMemo(
    () =>
      dateFiltrateGlobal.reduce(
        (acc, curr) => acc + (parseFloat(curr.valoare) || 0),
        0,
      ),
    [dateFiltrateGlobal],
  );
  const nrContracte = dateFiltrateGlobal.length;

  const stocDinamic = useMemo(() => {
    if (!partizi) return 0;
    const partiziUniceProcesate = new Set();
    let sumaStoc = 0;

    dateFiltrateGlobal.forEach((contract) => {
      const ids =
        contract.partiziAferente ||
        (contract.partidaId ? [contract.partidaId] : []);
      ids.forEach((id) => {
        if (!partiziUniceProcesate.has(String(id))) {
          const p = partizi.find((x) => String(x.id) === String(id));
          if (p?.stoc?.toString().toLowerCase().trim() === "da") {
            sumaStoc += parseFloat(p.volBrut || 0);
          }
          partiziUniceProcesate.add(String(id));
        }
      });
    });
    return sumaStoc;
  }, [dateFiltrateGlobal, partizi]);

  const CULORI = [ "#00b4d8", "#00f5d4", "#25a18e", "#ffbc42", "#9b5de5", "#f15bb5" ];
  const proprietari = ["Stat", "UAT", "AD"];

 const [activeIndex, setActiveIndex] = useState(null);

 const { dateNaturaProdus, volumTotalNatura } = useMemo(() => {
   const harta = {};
   let total = 0;

   dateFiltrateGlobal.forEach((curr) => {
     const idPartida = curr.partiziAferente?.[0] || curr.partidaId;
     const p = partizi?.find((x) => String(x.id) === String(idPartida));
     const natura = p?.naturaProdus || p?.natura || "Nespecificată";
     const vol = parseFloat(curr.volum) || 0;

     harta[natura] = (harta[natura] || 0) + vol;
     total += vol;
   });

   const ArrayDate = Object.keys(harta).map((key) => ({
     name: key,
     value: parseFloat(harta[key].toFixed(2)),
   }));

   return { dateNaturaProdus: ArrayDate, volumTotalNatura: total };
 }, [dateFiltrateGlobal, partizi]);

 // Determină dinamic textul din centrul cercului
 const dateMijloc = useMemo(() => {
   if (activeIndex !== null && dateNaturaProdus[activeIndex]) {
     const item = dateNaturaProdus[activeIndex];
     const procent =
       volumTotalNatura > 0
         ? ((item.value / volumTotalNatura) * 100).toFixed(1)
         : 0;
     return {
       eticheta: item.name,
       valoare: `${item.value.toLocaleString("ro-RO")} mc`,
       procent: `${procent}%`,
     };
   }
   return {
     eticheta: "Volum total",
     valoare: `${parseFloat(volumTotalNatura.toFixed(2)).toLocaleString("ro-RO")} mc`,
     procent: "100%",
   };
 }, [activeIndex, dateNaturaProdus, volumTotalNatura]);

  const dateGraficOcoale = useMemo(() => {
    return dateFiltrateGlobal
      .reduce((acc, curr) => {
        const ocolNume = curr.ocol || "Nespecificat";
        const volumNumeric = parseFloat(curr.volum) || 0;
        const existent = acc.find((item) => item.ocol === ocolNume);
        if (existent) {
          existent.volum = parseFloat(
            (existent.volum + volumNumeric).toFixed(2),
          );
        } else {
          acc.push({ ocol: ocolNume, volum: volumNumeric });
        }
        return acc;
      }, [])
      .sort((a, b) => b.volum - a.volum);
  }, [dateFiltrateGlobal]);

  // --- CUSTOM TOOLTIP ---
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div
        style={{
          background: isDark ? "#0f172a" : "#ffffff",
          border: isDark
            ? "1px solid rgba(255,255,255,0.15)"
            : "1px solid rgba(0,0,0,0.1)",
          padding: "10px 14px",
          borderRadius: "8px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
          color: isDark ? "#f8fafc" : "#0f172a",
          pointerEvents: "none",
          position: "relative",
          zIndex: 9999,
        }}
      >
        <p
          className="mb-1 text-uppercase fw-bold"
          style={{ fontSize: "0.65rem" }}
        >
          {payload[0].name || payload[0].payload.ocol || "Specificat"}
        </p>
        <h6 className="m-0 fw-bold" style={{ color: "#10b981" }}>
          {Number(payload[0].value || payload[0].payload.volum).toLocaleString(
            "ro-RO",
          )}{" "}
          <span
            className="fw-normal"
            style={{ fontSize: "0.75rem" }}
          >
            mc
          </span>
        </h6>
      </div>
    );
  };

  // Clase dinamice pentru temă
  const selectThemeClass = isDark
    ? "bg-dark text-light border-0"
    : "bg-light text-dark border-0";

  return (
    <Container
      fluid
      className="px-4 py-4"
      style={{
        backgroundColor: isDark ? "#090d16" : "#f8fafc",
        minHeight: "100vh",
      }}
    >
      {/* HEADER */}
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4">
        <div>
          <span
            className="text-success fw-bold text-uppercase tracking-wider"
            style={{ fontSize: "0.72rem" }}
          >
            Sistem Monitorizare contracte Masă Lemnoasă
          </span>
          <h2
            className="fw-bold m-0"
            style={{
              color: isDark ? "#fff" : "#0f172a",
              letterSpacing: "-0.5px",
            }}
          >
            Panou central{" "}
            <span className="fs-5 fw-light">| Direcția Silvică Mureș</span>
          </h2>
        </div>
        <div
          className="mt-2 mt-md-0 p-2 px-3 rounded-3 d-flex align-items-center gap-2 shadow-sm"
          style={{
            background: isDark ? "#1e293b" : "#fff",
            border: "1px solid rgba(0,0,0,0.05)",
          }}
        >
          <div
            style={{
              width: "8px",
              height: "8px",
              backgroundColor: "#10b981",
              borderRadius: "50%",
            }}
          ></div>
          <span className="small fw-semibold">
            {user?.nume} ({user?.rol})
          </span>
        </div>
      </div>

      {/* SECȚIUNEA FILTRE*/}
      <div
        className="p-3 mb-4 rounded-4 shadow-sm"
        style={{
          background: isDark ? "#111827" : "#fff",
          border: isDark ? "1px solid #1f2937" : "1px solid #e2e8f0",
        }}
      >
        <Row className="g-2">
          <Col xs={12} sm={6} md={2}>
            <InputGroup size="sm">
              <InputGroup.Text
                className={
                  isDark
                    ? "bg-secondary text-light border-0"
                    : "bg-light border-0"
                }
              >
                <Calendar3 size={13} />
              </InputGroup.Text>
              <Form.Select
                className={selectThemeClass}
                value={filtruAn}
                onChange={(e) => setFiltruAn(e.target.value)}
              >
                <option value="Toti">Toți Anii</option>
                {[
                  ...new Set(
                    datePermise.map((d) =>
                      new Date(
                        d.dataCreare?.seconds
                          ? d.dataCreare.seconds * 1000
                          : d.dataCreare,
                      ).getFullYear(),
                    ),
                  ),
                ]
                  .sort((a, b) => b - a)
                  .map((an) => (
                    <option key={an} value={an}>
                      {an}
                    </option>
                  ))}
              </Form.Select>
            </InputGroup>
          </Col>

          <Col xs={12} sm={6} md={2}>
            <InputGroup size="sm">
              <InputGroup.Text
                className={
                  isDark
                    ? "bg-secondary text-light border-0"
                    : "bg-light border-0"
                }
              >
                <PCircle size={13} />
              </InputGroup.Text>
              <Form.Select
                className={selectThemeClass}
                value={filtruProprietar}
                onChange={(e) => setFiltruProprietar(e.target.value)}
              >
                <option value="Toti">Toți Proprietarii</option>
                {proprietari.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </Form.Select>
            </InputGroup>
          </Col>

          <Col xs={12} sm={6} md={2}>
            <InputGroup size="sm">
              <InputGroup.Text
                className={
                  isDark
                    ? "bg-secondary text-light border-0"
                    : "bg-light border-0"
                }
              >
                <Journals size={13} />
              </InputGroup.Text>
              <Form.Select
                className={selectThemeClass}
                value={filtruTip}
                onChange={(e) => setFiltruTip(e.target.value)}
              >
                <option value="Toate">Toate Contractele</option>
                <option value="Prestari">Prestări</option>
                <option value="Regie">Regie</option>
              </Form.Select>
            </InputGroup>
          </Col>

          <Col xs={12} sm={6} md={2}>
            <InputGroup size="sm">
              <InputGroup.Text
                className={
                  isDark
                    ? "bg-secondary text-light border-0"
                    : "bg-light border-0"
                }
              >
                <Stripe size={13} />
              </InputGroup.Text>
              <Form.Select
                className={selectThemeClass}
                value={filtruStoc}
                onChange={(e) => setFiltruStoc(e.target.value)}
              >
                <option value="Toate">Tot</option>
                <option value="da">Cu Stoc (Da)</option>
                <option value="nu">Fără Stoc (Nu)</option>
              </Form.Select>
            </InputGroup>
          </Col>

          <Col xs={12} md={4}>
            <InputGroup size="sm">
              <InputGroup.Text
                className={
                  isDark
                    ? "bg-secondary text-light border-0"
                    : "bg-light border-0"
                }
              >
                <Tree size={13} />
              </InputGroup.Text>
              <Form.Select
                className={selectThemeClass}
                value={user?.rol === "administrator" ? filtruOcol : user?.ocol}
                onChange={(e) => setFiltruOcol(e.target.value)}
                disabled={user?.rol !== "administrator"}
              >
                <option value="Toate">Toate Ocoalele Silvice</option>
                {[...new Set(datePermise.map((d) => d.ocol))]
                  .sort()
                  .map((oc) => (
                    <option key={oc} value={oc}>
                      {oc}
                    </option>
                  ))}
              </Form.Select>
            </InputGroup>
          </Col>
        </Row>
      </div>

      {/* CARDURI METRICI */}
      <Row className="g-3 mb-4">
        <MetricCard
          title="Volum Total"
          value={totalVolum}
          unit="mc"
          icon={<Stack />}
          grad="linear-gradient(135deg, #059669, #10b981)"
          isDark={isDark}
        />
        <MetricCard
          title="Volum stoc"
          value={stocDinamic}
          unit="mc"
          icon={<Filter />}
          grad="linear-gradient(135deg, #0284c7, #38bdf8)"
          isDark={isDark}
        />
        <MetricCard
          title="Valoare totală"
          value={totalValoare}
          unit="RON"
          icon={<Cash />}
          grad="linear-gradient(135deg, #b45309, #f59e0b)"
          isDark={isDark}
        />
        <MetricCard
          title="Contracte"
          value={nrContracte}
          unit="buc"
          icon={<Journals />}
          grad="linear-gradient(135deg, #4f46e5, #6366f1)"
          isDark={isDark}
          as={Link}
          to="/prestari"
        
        />
      </Row>

      {/* GRAFICE RECHARTS */}
      <Row className="g-4">
        <Col lg={8}>
          <Card
            className="border-0 p-4 shadow"
            style={{
              borderRadius: "16px",
              background: isDark ? "#111827" : "#fff",
            }}
          >
            <h6
              className="fw-bold mb-1"
              style={{ color: isDark ? "#f8fafc" : "#0f172a" }}
            >
              Distribuția volumului pe Ocoale Silvice
            </h6>

            <div style={{ height: 350, width: "100%" }}>
              {dateGraficOcoale.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={dateGraficOcoale}
                    barCategoryGap="20%"
                    margin={{ top: 10, right: 10, left: -25, bottom: 25 }}
                  >
                    <defs>
                      <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                        <stop
                          offset="100%"
                          stopColor="#059669"
                          stopOpacity={0.15}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9"}
                    />
                    <XAxis
                      dataKey="ocol"
                      tick={{
                        fontSize: 10,
                        fill: isDark ? "#94a3b8" : "#64748b",
                      }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{
                        fontSize: 10,
                        fill: isDark ? "#94a3b8" : "#64748b",
                      }}
                      axisLine={false}
                      tickLine={false}
                    />

                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{
                        fill: isDark
                          ? "rgba(255,255,255,0.03)"
                          : "rgba(0,0,0,0.02)",
                      }}
                      useTranslate3d={true}
                    />
                    <Bar
                      dataKey="volum"
                      fill="url(#barGrad)"
                      radius={[6, 6, 0, 0]}
                      maxBarSize={350}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="d-flex h-100 align-items-center justify-content-center small">
                  Nu există date conform selecției.
                </div>
              )}
            </div>
          </Card>
        </Col>

        <Col lg={4}>
          <Card
            className="border-0 p-4 shadow h-100"
            style={{
              borderRadius: "16px",
              background: isDark ? "#111827" : "#fff",
            }}
          >
            <h6
              className="fw-bold mb-1"
              style={{ color: isDark ? "#f8fafc" : "#0f172a" }}
            >
              Natură Produs
            </h6>
            <span className=" d-block small mb-2">
              Structura masei lemnoaselor contractate
            </span>

            <div
              style={{ height: 320 }}
              className="d-flex flex-column justify-content-between align-items-center position-relative"
            >
              {/* CENTRU DINAMIC */}
              <div
                className="position-relative w-100 d-flex align-items-center justify-content-center"
                style={{ height: "70%" }}
              >
                <div
                  className="position-absolute text-center d-flex flex-column justify-content-center align-items-center"
                  style={{
                    pointerEvents: "none",
                    width: "110px",
                    height: "110px",
                    zIndex: 10,
                  }}
                >
                  <span
                    className="text-uppercase fw-semibold d-block text-truncate w-100"
                    style={{ fontSize: "0.62rem" }}
                  >
                    {dateMijloc.eticheta}
                  </span>
                  <span
                    className="fw-bold my-1"
                    style={{
                      fontSize: "0.95rem",
                      color: isDark ? "#fff" : "#0f172a",
                    }}
                  >
                    {dateMijloc.valoare}
                  </span>
                  <span
                    className="badge bg-success-subtle text-success border border-success-subtle rounded-pill px-2 py-0.5"
                    style={{ fontSize: "0.65rem" }}
                  >
                    {dateMijloc.procent}
                  </span>
                </div>

                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={dateNaturaProdus}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={85}
                      innerRadius={68}
                      paddingAngle={3}
                      onMouseEnter={(_, index) => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(null)}
                    >
                      {dateNaturaProdus.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CULORI[index % CULORI.length]}
                          stroke={isDark ? "#111827" : "#fff"}
                          strokeWidth={2}
                          style={{
                            opacity:
                              activeIndex === null
                                ? 1
                                : activeIndex === index
                                  ? 1
                                  : 0.35,
                            cursor: "pointer",
                            transition: "opacity 0.2s ease",
                          }}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* LEGENDĂ CU SINCRONIZARE HOVER */}
              <div
                className="w-100 overflow-auto px-1 mt-2"
                style={{ maxHeight: "105px" }}
              >
                {dateNaturaProdus.map((entry, index) => {
                  const prc =
                    volumTotalNatura > 0
                      ? ((entry.value / volumTotalNatura) * 100).toFixed(1)
                      : 0;
                  const esteActv = activeIndex === index;

                  return (
                    <div
                      key={entry.name}
                      className="d-flex justify-content-between align-items-center mb-1 p-1.5 rounded transition-all"
                      style={{
                        backgroundColor: esteActv
                          ? isDark
                            ? "rgba(16, 185, 129, 0.15)"
                            : "rgba(16, 185, 129, 0.08)"
                          : isDark
                            ? "rgba(255,255,255,0.02)"
                            : "rgba(0,0,0,0.01)",
                        borderLeft: esteActv
                          ? `3px solid ${CULORI[index % CULORI.length]}`
                          : "3px solid transparent",
                        fontSize: "0.72rem",
                        cursor: "pointer",
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseLeave={() => setActiveIndex(null)}
                    >
                      <div className="d-flex align-items-center text-truncate me-2">
                        <div
                          style={{
                            width: "8px",
                            height: "8px",
                            backgroundColor: CULORI[index % CULORI.length],
                            borderRadius: "50%",
                            marginRight: "8px",
                            flexShrink: 0,
                          }}
                        ></div>
                        <span
                          className={`text-truncate ${esteActv ? "fw-bold" : ""}`}
                        >
                          {entry.name}
                        </span>
                      </div>
                      <div className="text-end flex-shrink-0">
                        <span className="me-2" style={{ fontSize: "0.65rem" }}>
                          {prc}%
                        </span>
                        <strong className={isDark ? "text-light" : "text-dark"}>
                          {entry.value.toLocaleString("ro-RO")} mc
                        </strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

// Subcomponenta de Card Metrică
const MetricCard = ({ 
  title, value, unit, icon, grad, isDark, ...props }) => {
  const getHoverColors = () => {
    if (isDark) {
      switch (title) {
        case "Volum Total":
          return {
            border: "#10b981",
            wave: "rgba(16, 185, 129, 0.08)",
            shadow: "rgba(16, 185, 129, 0.25)",
          };
        case "Volum stoc":
          return {
            border: "#38bdf8",
            wave: "rgba(56, 189, 248, 0.08)",
            shadow: "rgba(56, 189, 248, 0.25)",
          };
        case "Valoare totală":
          return {
            border: "#f59e0b",
            wave: "rgba(245, 158, 11, 0.07)",
            shadow: "rgba(245, 158, 11, 0.2)",
          };
        case "Contracte":
          return {
            border: "#6366f1",
            wave: "rgba(99, 102, 241, 0.08)",
            shadow: "rgba(99, 102, 241, 0.25)",
          };
        default:
          return {
            border: "#10b981",
            wave: "rgba(16, 185, 129, 0.05)",
            shadow: "transparent",
          };
      }
    } else {
      switch (title) {
        case "Volum Total":
          return {
            border: "#059669",
            wave: "rgba(5, 150, 105, 0.04)",
            shadow: "rgba(5, 150, 105, 0.12)",
          };
        case "Volum stoc":
          return {
            border: "#0284c7",
            wave: "rgba(2, 132, 199, 0.04)",
            shadow: "rgba(2, 132, 199, 0.12)",
          };
        case "Valoare totală":
          return {
            border: "#b45309",
            wave: "rgba(180, 83, 9, 0.04)",
            shadow: "rgba(180, 83, 9, 0.1)",
          };
        case "Contracte":
          return {
            border: "#4f46e5",
            wave: "rgba(79, 70, 229, 0.04)",
            shadow: "rgba(79, 70, 229, 0.12)",
          };
        default:
          return {
            border: "#059669",
            wave: "rgba(5, 150, 105, 0.03)",
            shadow: "transparent",
          };
      }
    }
  };

  const culori = getHoverColors();

  return (
    <Col xs={12} sm={6} md={3}>
      <Card
        {...props}
        className={`border-1 h-100 position-relative overflow-hidden shadow-sm metric-card-custom ${props.to ? "metric-card-clickable" : ""}`}
        style={{
          borderRadius: "16px",
          backgroundColor: isDark ? "#111827" : "#fff",
          border: isDark
            ? "1px solid rgba(255,255,255,0.05)"
            : "1px solid rgba(0,0,0,0.05)",
          "--hover-bg": culori.border,
          "--hover-wave": culori.wave,
          "--hover-shadow": culori.shadow,
        }}
      >
        <div
          className="metric-card-glow"
          style={{ background: grad, opacity: isDark ? 0.12 : 0.07 }}
        />

        <Card.Body className="p-3 d-flex flex-column justify-content-between metric-card-body">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <span
              className="fw-bold text-uppercase"
              style={{ fontSize: "0.65rem", letterSpacing: "0.5px" }}
            >
              {title}
            </span>
            <div
              className="p-2 rounded-3 d-flex align-items-center justify-content-center text-white"
              style={{ background: grad, width: "32px", height: "32px" }}
            >
              {React.cloneElement(icon, { size: 16 })}
            </div>
          </div>

          <div>
            <h4
              className="mb-0 fw-bold tracking-tight"
              style={{ color: isDark ? "#fff" : "#0f172a" }}
            >
              {value.toLocaleString("ro-RO", { maximumFractionDigits: 2 })}
              <span
                className="fw-normal ms-1 text-muted"
                style={{ fontSize: "0.8rem" }}
              >
                {unit}
              </span>
            </h4>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default Dashboard;
